// SCR-SETS (E05-T07, ux/05 §7) и MOD-S8-TOKENS (E05-T18, ux/05 §11). Собранное — из сумки (GET /inventory),
// активные пороги бонусов — из `setBonuses` сервера; число частей — канон §8 (content/sets.ts). Жетоны
// председателя — GET /sets/s8/tokens: норма, обмены и неделя приходят с сервера, клиент их не считает.
import { useState } from 'preact/hooks';
import type { Api, ResponseOf } from '@garages/contract';
import { useApp } from '../app/app.js';
import { RARITY_NAMES, SLOT_NAMES, itemName } from '../content/catalog.js';
import { SETS, s8Piece, slotOf, type SetInfo } from '../content/sets.js';
import type { Item, Slot } from '../content/types.js';
import { t as tr } from '../i18n/t.js';
import { ApiCallError } from '../net/api-client.js';
import { inventory, online, player, saveCache, setInventoryItems, setWallet } from '../state/game.js';
import type { ScreenProps, SheetProps } from '../ui/App.js';
import { ErrorBanner, ScreenTitle, Sheet, useActionBar } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { useLoad } from '../ui/use-load.js';
import { ICONS } from '../assets/icons.js';
import { Icon } from '../assets/ui.js';
import { useInventory } from './char.js';
import '../ui/store.css';

type Tokens = ResponseOf<Api['getS8Tokens']>;
type SetBonus = NonNullable<ResponseOf<Api['getInventory']>['setBonuses']>[number];
const S8_LEVEL = 30;
const S8_MAX = 6;

export interface SetView { set: SetInfo; owned: Set<string>; worn: number; tiers: { n: number; text: string; active: boolean; lack: number }[]; full: boolean }

/**
 * Состояние сета: собранные части (разные ID в сумке), надетые; порог активен — по серверу (`setBonuses`),
 * если сервер его прислал, иначе по числу надетых частей.
 */
export function setView(set: SetInfo, items: Pick<Item, 'defId' | 'equipped'>[], bonuses: SetBonus[] = []): SetView {
  const owned = new Set(items.filter((i) => set.pieces.includes(i.defId)).map((i) => i.defId));
  const srv = bonuses.find((b) => b.setId === set.id);
  const worn = srv?.pieces ?? new Set(items.filter((i) => i.equipped && set.pieces.includes(i.defId)).map((i) => i.defId)).size;
  return {
    set, owned, worn, full: owned.size === set.pieces.length,
    tiers: set.tiers.map((t) => {
      const active = srv?.tiers.find((x) => x.pieces === t.n)?.active ?? worn >= t.n;
      return { ...t, active, lack: Math.max(0, t.n - worn) };
    }),
  };
}

/** Что мешает обмену жетонов (ux/05 §11): уровень, норма, неделя, 6 из 6. null — можно менять. */
export function s8Block(t: Pick<Tokens, 'tokens' | 'norm' | 'exchanges' | 'weeklyAvailable'>, level: number): 'level' | 'done' | 'tokens' | 'weekly' | null {
  if (t.exchanges.length >= S8_MAX) return 'done';
  if (level < S8_LEVEL) return 'level';
  if (t.tokens < t.norm) return 'tokens';
  if (!t.weeklyAvailable) return 'weekly';
  return null;
}

function PieceIcon(p: { defId: string; have: boolean; size?: number }) {
  return (
    <span class={`set-piece ${p.have ? 'have' : 'miss'}`} title={itemName(p.defId)}>
      <Icon id={p.defId} fallback={ICONS[slotOf(p.defId)][1]} size={p.size ?? 28} />
    </span>
  );
}

function S8Block(p: { tokens: Tokens | null; error: boolean }) {
  const app = useApp();
  const t = p.tokens;
  const lvl = player.value?.level ?? 1;
  if (!t) return p.error ? <span class="small muted">{tr('CL-S8-ERR')}</span> : <span class="skeleton" style={{ height: 36 }} />;
  const block = s8Block(t, lvl);
  if (block === 'done') return <b class="pos" data-testid="s8-done">{tr('CL-S8-DONE')}</b>;
  return (
    <div class="col" style={{ gap: 4 }} data-testid="s8-tokens">
      <span>{tr('CL-S8-COUNTER', { n: t.tokens, norm: t.norm })}</span>
      <div class="progress gold"><i style={{ width: `${Math.min(100, (t.tokens / t.norm) * 100)}%` }} /></div>
      <span class="small muted">{lvl < S8_LEVEL ? tr('CL-S8-LOCKED') : tr('CL-S8-HINT')}</span>
      {lvl >= S8_LEVEL ? (
        <button class={`btn small ${block ? 'outline' : 'accent'}`} data-testid="s8-open" disabled={!online.value} onClick={(e) => { e.stopPropagation(); app.nav.openSheet('MOD-S8-TOKENS'); }}>
          {tr('CL-S8-EXCHANGE')}{block === null ? ' !' : ''}
        </button>
      ) : null}
    </div>
  );
}

function SetCard(p: { v: SetView; open: boolean; onToggle: () => void; s8?: { tokens: Tokens | null; error: boolean } }) {
  const { v } = p;
  const s8 = v.set.id === 'S8';
  return (
    <div class={`card set-card rarity-${v.set.rarity} ${s8 ? 's8' : ''}`} data-testid={`set-${v.set.id}`}>
      <button class="set-head" onClick={p.onToggle} aria-expanded={p.open}>
        <span class="col" style={{ gap: 2, flex: 1, textAlign: 'left' }}>
          <b>{v.set.name}</b>
          <span class="small" style={{ color: 'var(--r)' }}>{RARITY_NAMES[v.set.rarity]} · {tr('CL-SETS-FROM-LVL', { n: v.set.level })}</span>
        </span>
        <span class="badge soft" data-testid={`set-count-${v.set.id}`}>{tr('CL-SETS-COUNT', { k: v.owned.size, n: v.set.pieces.length })}</span>
      </button>
      <div class="set-pieces">{v.set.pieces.map((d) => <PieceIcon key={d} defId={d} have={v.owned.has(d)} />)}</div>
      {!p.open ? (
        <span class="small set-tiers-short" data-testid={`tiers-short-${v.set.id}`}>
          {v.tiers.map((t) => <span key={t.n} class={t.active ? 'pos' : 'muted'}>{t.active ? '● ' : '○ '}{t.n === v.set.pieces.length ? tr('CL-SETS-FULL') : `${t.n}:`} </span>)}
          <span class="muted">{tr('CL-SETS-MORE')}</span>
        </span>
      ) : null}
      {p.open ? <ul class="set-tiers">
        {v.tiers.map((t) => (
          <li key={t.n} class={t.active ? 'on' : 'off'} data-testid={`tier-${v.set.id}-${t.n}`}>
            <b>{t.n === v.set.pieces.length ? tr('CL-SETS-FULL') : `${t.n}:`}</b> {t.text}
            {!t.active ? <span class="small muted"> · {tr('CL-SETS-LACK', { n: t.lack })}</span> : null}
          </li>
        ))}
      </ul> : null}
      {v.worn === v.set.pieces.length ? <span class="badge" data-testid={`set-worn-${v.set.id}`}>{tr('CL-SETS-WORN-FULL')}</span> : null}
      {s8 && p.s8 ? <S8Block tokens={p.s8.tokens} error={p.s8.error} /> : null}
      {p.open ? (
        <div class="col set-more" style={{ gap: 4 }}>
          {v.set.pieces.filter((d) => !v.owned.has(d)).map((d) => (
            <span key={d} class="small">{SLOT_NAMES[slotOf(d)]}: {itemName(d)} — <span class="muted">{v.set.source}</span></span>
          ))}
          {v.full ? <span class="small pos">{tr('CL-SETS-ALL-HAVE')}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

export function SetsScreen(_p: ScreenProps) {
  const app = useApp();
  const inv = useInventory();
  const tokens = useLoad(() => app.call('getS8Tokens'));
  const [open, setOpen] = useState<string | null>(null);
  const items = inv.data?.items ?? [];
  const views = SETS.map((s) => setView(s, items, inv.data?.setBonuses));
  const openView = views.find((v) => v.set.id === open);
  // Развёрнута карточка с полным комплектом — «Надеть сет „{name}“» (ux/05 §7).
  const wearAll = async (v: SetView) => {
    try {
      for (const d of v.set.pieces) {
        const it = items.filter((i) => i.defId === d).sort((a, b) => b.upgradeLevel - a.upgradeLevel)[0];
        if (!it || it.equipped) continue;
        const r = await app.call('equip', { body: { itemId: it.itemId } });
        setInventoryItems((xs) => xs.map((x) => ({ ...x, equipped: r.equipped.some((e) => e.itemId === x.itemId) })));
        if (player.value) player.value = { ...player.value, derived: r.stats };
      }
      saveCache();
      app.haptic('success');
    } catch (e) { app.handleError(e, { resource: tr('CL-SCREENS-INVENTORY-16') }); }
  };
  useActionBar({
    back: true,
    ...(openView && openView.full && openView.worn < openView.set.pieces.length
      ? { main: { text: tr('CL-SETS-WEAR', { name: openView.set.name }), testId: 'set-wear', disabled: !online.value, onClick: () => wearAll(openView) } } : {}),
  });
  return (
    <>
      <ScreenTitle>{tr('CL-SETS-TITLE')}</ScreenTitle>
      <div class="content screen-bg" data-testid="SCR-SETS">
        <div class="page col store-page">
          <div class="store-panel sets-panel col">
          {inv.error && !inv.offline ? <ErrorBanner what={tr('CL-SETS-TITLE')} onRetry={inv.reload} /> : null}
          {inv.offline ? <div class="banner">{tr('CL-SETS-OFFLINE')}</div> : null}
          {!inv.data && inv.loading ? [0, 1, 2].map((i) => <div key={i} class="skeleton" style={{ height: 96 }} />) : null}
          {views.map((v) => (
            <SetCard key={v.set.id} v={v} open={open === v.set.id} onToggle={() => { app.haptic('selection'); setOpen(open === v.set.id ? null : v.set.id); }}
              {...(v.set.id === 'S8' ? { s8: { tokens: tokens.data, error: !!tokens.error } } : {})} />
          ))}
          </div>
        </div>
      </div>
    </>
  );
}

/** MOD-S8-TOKENS: шесть слотов S8, недостающие подсвечены (выбрать можно любой), «Обменов: k из 6». */
export function S8TokensSheet({ entry }: SheetProps) {
  const app = useApp();
  const load = useLoad(() => app.call('getS8Tokens'));
  const t = load.data;
  const items = inventory.value?.items ?? [];
  const have = new Set(items.map((i) => i.defId));
  const slots: Slot[] = ['head', 'jacket', 'pants', 'shoes', 'tool', 'accessory'];
  const [slot, setSlot] = useState<Slot>(() => slots.find((s) => !have.has(s8Piece(s))) ?? 'head');
  const [key] = useState(() => ({ k: app.idem() }));
  const lvl = player.value?.level ?? 1;
  const block = t ? s8Block(t, lvl) : null;
  const piece = s8Piece(slot);
  const swap = async () => {
    try {
      const r = await app.call('exchangeS8', { body: { slot }, idempotencyKey: key.k });
      key.k = app.idem();
      setWallet(r.wallet);
      setInventoryItems((xs) => [...xs, r.item]);
      saveCache();
      app.haptic('success');
      app.nav.openSheet('MOD-REWARD', { grant: { items: [{ defId: r.item.defId, qty: 1 }] } }, { replace: true });
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : 'INTERNAL';
      if (code !== 'NETWORK' && code !== 'TIMEOUT') key.k = app.idem();
      const text: Record<string, string> = { NOT_ENOUGH_S8_TOKENS: 'CL-S8-ERR-TOKENS', WEEKLY_LIMIT: 'CL-S8-WEEKLY', LIMIT_REACHED: 'CL-S8-DONE', LEVEL_TOO_LOW: 'CL-S8-LOCKED' };
      if (text[code]) { app.haptic('error'); showToast(tr(text[code]!), 'error'); load.reload(); return; }
      app.handleError(e, { resource: tr('CL-S8-SAFE') });
    }
  };
  let cta: { text: string; disabled: boolean };
  if (!online.value) cta = { text: tr('UI-OFFLINE'), disabled: true };
  else if (!t) cta = { text: tr('CL-S8-EXCHANGE'), disabled: true };
  else if (block === 'level') cta = { text: tr('CL-S8-LOCKED'), disabled: true };
  else if (block === 'tokens') cta = { text: tr('CL-S8-LACK', { n: t.norm - t.tokens }), disabled: true };
  else if (block === 'weekly') cta = { text: tr('CL-S8-WEEKLY'), disabled: true };
  else if (block === 'done') cta = { text: tr('CL-S8-DONE'), disabled: true };
  else cta = { text: tr('CL-S8-CTA', { norm: t.norm, item: itemName(piece) }), disabled: false };
  return (
    <Sheet entry={entry} title={tr('CL-S8-TITLE')} actions={[
      { text: tr('CL-CAR-CANCEL'), tone: 'outline', onClick: () => app.nav.popSheet() },
      { text: cta.text, testId: 's8-exchange', disabled: cta.disabled, onClick: swap },
    ]}>
      <div class="col" data-testid="MOD-S8-TOKENS-body">
        {load.error && !t ? <ErrorBanner what={tr('CL-S8-TITLE')} onRetry={load.reload} /> : null}
        <div class="s8-slots" role="radiogroup">
          {slots.map((s) => {
            const d = s8Piece(s);
            return (
              <button key={s} role="radio" aria-checked={slot === s} class={`s8-slot ${slot === s ? 'on' : ''} ${have.has(d) ? 'have' : 'miss'}`} data-testid={`s8-slot-${s}`} onClick={() => { app.haptic('selection'); setSlot(s); }}>
                <Icon id={d} fallback={ICONS[s][1]} size={32} />
                <span class="small">{SLOT_NAMES[s]}</span>
              </button>
            );
          })}
        </div>
        <div class="card col" style={{ gap: 2 }}>
          <b>{itemName(piece)}</b>
          <span class="small rarity-legendary" style={{ color: 'var(--r)' }}>{RARITY_NAMES.legendary} · {SLOT_NAMES[slot]} · {have.has(piece) ? tr('CL-S8-HAVE') : tr('CL-S8-MISSING')}</span>
          <span class="small muted">{tr('CL-S8-ROLL')}</span>
        </div>
        {t ? (
          <>
            <span data-testid="s8-balance">{tr('CL-S8-COUNTER', { n: t.tokens, norm: t.norm })}</span>
            <span data-testid="s8-exchanges">{tr('CL-S8-EXCHANGES', { k: t.exchanges.length, n: S8_MAX })}</span>
            {!t.weeklyAvailable && t.nextAt ? <span class="small muted" data-testid="s8-next">{tr('CL-S8-NEXT', { d: new Date(t.nextAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' }) })}</span> : null}
          </>
        ) : <span class="skeleton" style={{ height: 40 }} />}
      </div>
    </Sheet>
  );
}

/** Вход в SCR-SETS из SCR-CHAR: «!» — обмен жетонов доступен (ux/05 §11). */
export function SetsChip() {
  const app = useApp();
  const lvl = player.value?.level ?? 1;
  const tokens = useLoad(() => (lvl >= S8_LEVEL ? app.call('getS8Tokens') : Promise.resolve(null)));
  const ready = !!tokens.data && s8Block(tokens.data, lvl) === null;
  return (
    <button class="chip" data-testid="open-sets" onClick={() => app.nav.push('SCR-SETS')}>
      {tr('CL-SETS-TITLE')}{ready ? <span class="tab-dot" aria-label={tr('CL-S8-READY')} /> : null}
    </button>
  );
}
